import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TheBorder} from "../TheBorder";
import {TheBorderGameAssets} from "../TheBorderGameAssets";
import {TheBorderStatics} from "../TheBorderStatics";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {gsap} from "gsap";
import {TheBorderSymbol, TheBorderSymbolStates} from "../reels/TheBorderSymbol";
import {TheBorderSpine} from "../components/TheBorderSpine";
import {IBetClusterWin} from "../server/TheBorderData";
import {TheBorderXBombSymbol} from "../reels/TheBorderXBombSymbol";
import {IClusterData, TheBorderUtils} from "../utils/TheBorderUtils";
import {TheBorderSound} from "../sound/TheBorderSound";

/**
 * Created by Jerker Nord on 2022-05-02.
 */

export class TheBorderXNudgeView extends PIXI.Container implements StageLayer {

    private static ANIMATION_NAME: string = "Transform";

    private _symbolToWildAnimations: TheBorderSpine[];
    private _symbolToWildHalfAnimations: TheBorderSpine[];
    private _symInBetClusterWins: Set<string>;
    private _nudgeCtr: number = 1;
    private _allWrInEvalReel: number = 0;
    private _gridVisited: string[] = [];
    private _soundInterval: number;
    private _mainTl: gsap.core.Timeline;
    private _otherTls: gsap.core.Timeline[] = [];

    constructor() {
        super();
        this.name = "TheBorderXNudgeView";
        this.position = TheBorderStatics.MAIN_REELS_VIEW_POSITION;
        this.init();

        //Transform
        //L1, L2, L3, M1, M2, M3, M4, M5
    }

    public static getReelsData(): string[][] {
        const reels: string[][] = [];
        for (let i: number = 0; i < 6; i++) {
            reels[i] = [];
            let syms: TheBorderSymbol[] = TheBorder.allReelsView.mainReelModels[i].getAllSymbols() as TheBorderSymbol[];
            for (let j: number = 0; j < syms.length; j++) {
                reels[i][j] = syms[j].aliasName;
            }
        }

        return reels;
    }

    private static createNewSymbolToWildAnimation(assetName: string): TheBorderSpine {
        return new TheBorderSpine(TheBorder.assetLoader.getSpineAsset(assetName));
    }

    private static resetSpineItem(item: TheBorderSpine, symbol: string): void {
        if (!item.visible) {
            item.visible = true;
            item.updateTransform();
        }
        item.state.clearTracks();
        item.skeleton.setSkin(null);
        item.skeleton.setSkinByName(symbol)
        item.skeleton.setSlotsToSetupPose();
        item.skeleton.setToSetupPose();
        item.state.setEmptyAnimation(0, 0);
    }

    private static checkReelForPendingWR(reel: number): boolean {
        const allSymbols: TheBorderSymbol[] = (TheBorder.allReelsView.mainReelModels[reel].getAllSymbols() as TheBorderSymbol[]),
            allNamesInWrReel: string[] = TheBorder.model.evaluatedArea[reel];

        for (let i: number = 0; i < allNamesInWrReel.length; i++) {
            if (allNamesInWrReel[i].indexOf("WR") > -1 && allSymbols[i].aliasName.indexOf("WR") === -1) {
                return true;
            }
        }

        return false;
    }

    public getAllSymbolTransformAnimations(reel: number, row: number, finishCb: () => void): void {
        row--;
        const allSymbolToWildTL: gsap.core.Timeline = gsap.timeline(),
            betClusterWins: IBetClusterWin[] = TheBorder.model.betClusterWins,
            symInBetClusterWins: Set<string> = new Set<string>(),
            allSymbolsNoWin: boolean[][] = [],
            reels: string[][] = TheBorderXNudgeView.getReelsData(),
            checkedSymArr: string[] = [],
            allInitClusters: Set<string>[] = [],
            allNamesInWrReel: string[] = TheBorder.model.evaluatedArea[reel];
        let multiValue: number = 0,
            xPos: number = 0,
            yPos: number = 0;

        this._otherTls = [];

        // #182
        this._gridVisited = [];
        this._nudgeCtr = 1;
        this._allWrInEvalReel = 0;
        for (let i: number = 0; i < allNamesInWrReel.length; i++) {
            if (allNamesInWrReel[i].indexOf("WR") > -1) {
                this._allWrInEvalReel++;
            }
        }

        betClusterWins.forEach((cluster: IBetClusterWin) => {
            symInBetClusterWins.add(cluster.symbol);
        });
        this._symInBetClusterWins = symInBetClusterWins;

        symInBetClusterWins.forEach((name: string) => {
            const clusters: IClusterData[] = TheBorderUtils.getClustersForSymbol(reels, name, checkedSymArr);
            for (let i: number = 0; i < clusters.length; i++) {
                if (clusters[i].isWRInReels) {
                    allInitClusters.push(clusters[i].info);
                }
            }
        });

        for (let i: number = 0; i < 6; i++) {
            allSymbolsNoWin[i] = [];
            for (let j: number = 0; j < TheBorder.allReelsView.mainReelModels[i].getAllSymbols().length; j++) {
                allSymbolsNoWin[i][j] = true;
            }
        }

        allInitClusters.forEach((value: Set<string>) => {
            value.forEach((val: string) => {
                const grid: string[] = val.split("_");

                allSymbolsNoWin[+grid[0]][+grid[1]] = false;
            });
        });

        allSymbolsNoWin.forEach((values: boolean[], rl: number) => {
            values.forEach((value: boolean, rw: number) => {
                if (value) {
                    allSymbolToWildTL.add(() => {
                        (<TheBorderSymbol>TheBorder.allReelsView.getSymbolAtPosition(rl, rw)).getNoWinAnimation(0.2);
                    });
                }
            });
        });
        allSymbolToWildTL.to(this, {
            duration: 0.2
        });

        allInitClusters.forEach((value: Set<string>) => {
            let sym: TheBorderSymbol,
                hasNudged: boolean = false;

            value.forEach((val: string) => {
                const grid: string[] = val.split("_"),
                    rl: number = +grid[0],
                    rw: number = +grid[1];

                if (rw !== row && rl === reel) {
                    sym = (<TheBorderSymbol>TheBorder.allReelsView.getSymbolAtPosition(rl, rw));

                    if (this.canSymbolNudge(rl, rw, sym)) {
                        this._gridVisited.push(rl + "_" + rw);
                        hasNudged = true;
                        sym.hasNudged = true;
                        xPos = sym.position.x + (TheBorderStatics.reelPositions[rl].x);
                        yPos = sym.position.y;
                        if (sym.symbolName.indexOf("X_BOMB") > -1) {
                            (<TheBorderXBombSymbol>sym).turnOffSymbolSpine();
                        }
                        allSymbolToWildTL.add(() => {
                            this._nudgeCtr++;
                            multiValue += (TheBorder.allReelsView.getSymbolAtPosition(rl, rw)).splitLevel;
                            this.getSymbolToWildAnimation(reel, rw, TheBorder.allReelsView.getSymbolAtPosition(rl, rw).symbolName);
                        });
                    }
                }
            });

            if (hasNudged) {
                allSymbolToWildTL.to(this, {
                    duration: 1.1
                });
                allSymbolToWildTL.add(TheBorder.swooshAnimation.playSwoosh(xPos, yPos, 610, -84));
                allSymbolToWildTL.add(() => {
                    TheBorder.winMultiplierView.addToWinMultiplier(multiValue);
                    multiValue = 0;

                    this.checkClustersAfterWards(reel, row, finishCb);
                });
            }
        });

        this._mainTl = allSymbolToWildTL;
    }

    public abort(): void {
        if (this._mainTl) {
            this._mainTl.totalProgress(1);
        }
        this._otherTls.forEach((tl: gsap.core.Timeline) => {
            if (tl) {
                tl.totalProgress(1);
            }
        });
    }

    private init(): void {
        this._symbolToWildAnimations = [];
        for (let i: number = 0; i < 12; i++) {
            let tempSpine: TheBorderSpine = TheBorderXNudgeView.createNewSymbolToWildAnimation(TheBorderGameAssets.SYMBOL_TO_WILD_SPINE);
            tempSpine.visible = false;
            this.addChild(tempSpine);
            this._symbolToWildAnimations.push(tempSpine);
        }

        this._symbolToWildHalfAnimations = [];
        for (let i: number = 0; i < 12; i++) {
            let tempSpine: TheBorderSpine = TheBorderXNudgeView.createNewSymbolToWildAnimation(TheBorderGameAssets.SYMBOL_TO_WILD_HALF_SPINE);
            tempSpine.visible = false;
            this.addChild(tempSpine);
            this._symbolToWildHalfAnimations.push(tempSpine);
        }
    }

    private canSymbolNudge(rl: number, rw: number, sym: TheBorderSymbol): boolean {
        let symAbove: TheBorderSymbol | boolean = false,
            symBelow: TheBorderSymbol | boolean = false;

        if ((rw - 1) > -1) {
            symAbove = (<TheBorderSymbol>TheBorder.allReelsView.getSymbolAtPosition(rl, rw - 1));
        }
        if ((rw + 1) < TheBorder.allReelsView.getReelLength(rl)) {
            symBelow = (<TheBorderSymbol>TheBorder.allReelsView.getSymbolAtPosition(rl, rw + 1));
        }

        return (this._gridVisited.indexOf(rl + "_" + rw) === -1 && sym && !sym.hasNudged && ((symAbove && symAbove.aliasName.indexOf("WR") > -1) || (symBelow && symBelow.aliasName.indexOf("WR") > -1)));
    }

    private checkClustersAfterWards(reel: number, row: number, finishCb: () => void): void {
        const tl: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    if (this._nudgeCtr === this._allWrInEvalReel) {
                        this._allWrInEvalReel = -1;
                        gsap.delayedCall(0.2, () => {
                            for (let reel: number = 0; reel < reels.length; reel++) {
                                for (let row: number = 0; row < reels[reel].length; row++) {
                                    (TheBorder.allReelsView.getSymbolAtPosition(reel, row) as TheBorderSymbol).getNoWinEndAnimation();
                                }
                            }
                        });
                        gsap.delayedCall(0.3, () => {
                            // #354
                            this._gridVisited.forEach((value: string) => {
                                const grid: string[] = value.split("_"),
                                    sym: TheBorderSymbol = (TheBorder.allReelsView.getSymbolAtPosition(+grid[0], +grid[1]) as TheBorderSymbol);

                                if (sym && sym.aliasName.indexOf("WR") === -1) {
                                    sym.aliasName = "WR";
                                    if (sym.sizeMultiplier === 0.5) {
                                        sym.aliasName = "WR_H";
                                    }
                                }
                            });

                            finishCb();
                        });
                    }
                }
            }),
            allClustersAfterwards: Set<string>[] = [],
            newWins: boolean[][] = [];
        let multiValue: number = 0,
            checkAgain: boolean = false,
            xPos: number,
            yPos: number;

        const reels: string[][] = TheBorderXNudgeView.getReelsData(),
            checkedSymArr: string[] = [];

        this._otherTls.push(tl);

        this._symInBetClusterWins.forEach((symbolName: string) => {
            const clusters: IClusterData[] = TheBorderUtils.getClustersForSymbol(reels, symbolName, checkedSymArr);
            for (let i: number = 0; i < clusters.length; i++) {
                if (clusters[i].isWRInReels) {
                    allClustersAfterwards.push(clusters[i].info);
                }
            }
        });

        for (let i: number = 0; i < 6; i++) {
            newWins[i] = [];
            for (let j: number = 0; j < TheBorder.allReelsView.mainReelModels[i].getAllSymbols().length; j++) {
                newWins[i][j] = false;
            }
        }

        allClustersAfterwards.forEach((value: Set<string>) => {
            value.forEach((val: string) => {
                const grid: string[] = val.split("_");

                newWins[+grid[0]][+grid[1]] = true;
            });
        });

        newWins.forEach((values: boolean[], rl: number) => {
            values.forEach((value: boolean, rw: number) => {
                if (value) {
                    (<TheBorderSymbol>TheBorder.allReelsView.getSymbolAtPosition(rl, rw)).getNoWinEndAnimation();
                }
            });
        });

        tl.to(this, {
            duration: 0.1
        });

        allClustersAfterwards.forEach((value: Set<string>) => {
            let sym: TheBorderSymbol;

            checkAgain = false;
            value.forEach((val: string) => {
                const grid: string[] = val.split("_"),
                    rl: number = +grid[0],
                    rw: number = +grid[1];

                if (rw !== row && rl === reel) {
                    sym = (<TheBorderSymbol>TheBorder.allReelsView.getSymbolAtPosition(rl, rw));

                    if (this.canSymbolNudge(rl, rw, sym) && sym.aliasName !== "WR") {
                        this._gridVisited.push(rl + "_" + rw);
                        checkAgain = true;
                        sym.hasNudged = true;
                        sym.aliasName = "WR";
                        xPos = sym.position.x + (TheBorderStatics.reelPositions[rl].x);
                        yPos = sym.position.y;
                        this.getSymbolToWildAnimation(reel, rw, sym.symbolName);
                        if (sym.symbolName.indexOf("X_BOMB") > -1) {
                            (<TheBorderXBombSymbol>sym).turnOffSymbolSpine();
                        }
                        tl.add(() => {
                            this._nudgeCtr++;
                            multiValue += (TheBorder.allReelsView.getSymbolAtPosition(rl, rw)).splitLevel;
                        });
                    }
                }
            });

            if (checkAgain) {
                tl.to(this, {
                    duration: 1.1
                });
                tl.add(TheBorder.swooshAnimation.playSwoosh(xPos, yPos, 610, -84));
                tl.add(() => {
                    TheBorder.winMultiplierView.addToWinMultiplier(multiValue);
                    multiValue = 0;
                });
            }
        });

        if (checkAgain || (TheBorderXNudgeView.checkReelForPendingWR(reel))) {
            tl.add(() => {
                this.checkClustersAfterWards(reel, row, finishCb);
            });
        }
    }

    private getXSymbolToWildAnimation(array: TheBorderSpine[], reel: number, row: number, yOffset: number): TheBorderSpine {
        for (let i: number = 0; i < array.length; i++) {
            if (!array[i].used) {
                array[i].used = true;
                array[i].position.x = TheBorder.allReelsView.mainReelModels[reel].reelView.position.x;
                array[i].position.y = (<TheBorderSymbol>TheBorder.allReelsView.getSymbolAtPosition(reel, row)).position.y + yOffset;
                return array[i];
            }
        }
        array.push(TheBorderXNudgeView.createNewSymbolToWildAnimation(TheBorderGameAssets.SYMBOL_TO_WILD_SPINE));
        return this.getXSymbolToWildAnimation(array, reel, row, yOffset);
    }

    private getSymbolToWildAnimation(reel: number, row: number, symbol: string): void {
        const symbolToWildTL: gsap.core.Timeline = gsap.timeline();
        let symbolToWildAnimation: TheBorderSpine;

        if (symbol.indexOf("_H") === -1) {
            symbolToWildAnimation = this.getXSymbolToWildAnimation(this._symbolToWildAnimations, reel, row, 0);
        } else {
            symbolToWildAnimation = this.getXSymbolToWildAnimation(this._symbolToWildHalfAnimations, reel, row, -25);
            symbol = symbol.split("_H")[0];
        }
        symbolToWildTL.add(() => {
            this.playNudgeSymbolSounds();
            TheBorderXNudgeView.resetSpineItem(symbolToWildAnimation, symbol);
        });
        symbolToWildTL.add(new SpineTween(symbolToWildAnimation, 0, TheBorderXNudgeView.ANIMATION_NAME, 0));
        symbolToWildTL.add(() => {
            (<TheBorderSymbol>TheBorder.allReelsView.getSymbolAtPosition(reel, row)).changeToWild();
            symbolToWildAnimation.visible = false;
            symbolToWildAnimation.state.clearTracks();
        });
    }

    private playNudgeSymbolSounds(): void {
        window.clearTimeout(this._soundInterval); //#245
        this._soundInterval = window.setTimeout(() => {
            TheBorder.soundPlayer.playSound(TheBorderSound.NUDGE_SYMBOL_SOUNDS[Math.floor(Math.random() * TheBorderSound.NUDGE_SYMBOL_SOUNDS.length)]);//#245, #50
        }, 10);
    }
}